import type { CentroPortalClient } from "@platform/api";
import type { CellReview } from "@platform/btx-plan-designer";

export type CellReviewFlagType =
  | "needs_review"
  | "broker_highlight"
  | "edited"
  | "difference_dismissed";

export interface CellReviewResponseRecord extends CellReview {
  benefitPlanId: string;
  benefitKey: string;
  coverageType?: string;
  propertyKey?: string;
  network?: string;
  reviewType?: CellReviewFlagType;
  /** Set only on difference_dismissed reviews. */
  dismissedValue?: string | null;
}

export interface ListCellReviewsResponse {
  rfpId: string;
  reviews: CellReviewResponseRecord[];
}

export type CellReviewScopeType = "proposal_plan" | "response" | "comps";

/**
 * Cache key for the unscoped, per-RFP cell-review list of one scope. Shared by
 * the Comps/Responses tabs and the Needs Review Comments drawer so a mutation
 * in one place invalidates the other's read of the same scope. Quotes builds
 * its own coverageType-scoped key instead (its list is narrower than this),
 * and separately triggers invalidation of this key so the drawer stays current.
 */
export const cellReviewsQueryKey = (
  scopeType: CellReviewScopeType,
  rfpId: string | undefined,
) => [`${scopeType}-cell-reviews`, rfpId] as const;

/**
 * Fetches one page of cell reviews from the centro-portal BFF. Shared by
 * `useCellReviewStore` (one tab, scoped to its own list URL) and the Needs
 * Review Comments drawer (all three scopes, unscoped by coverage).
 */
export const fetchCellReviews = async (
  centroPortalClient: CentroPortalClient,
  listUrl: string | undefined,
  rfpId: string | undefined,
): Promise<ListCellReviewsResponse> => {
  if (!listUrl) {
    return { rfpId: rfpId ?? "", reviews: [] };
  }

  const response = await centroPortalClient.get<
    ListCellReviewsResponse,
    unknown,
    true
  >({
    url: listUrl,
    responseType: "json",
    throwOnError: true,
  });

  return response.data;
};
